import type { ComponentType } from "react";
import ComponentTypes from "~/features/cms/componentTypes";
import type { CMSComponentProps } from "~/features/cms/types/props";
import type { CMSTeaserStandardProps, CMSTeaserPublicationProps } from "~/features/cms/types/model";
import {
  CMSComponentRendererProvider,
  initializeCMSComponentRenderer,
  type CMSComponentRenderer,
} from "~/features/cms/CMSComponentRendererContext";

// Components
import { CMSSection } from "~/features/cms/components/cmsSection/CMSSection";
import { CMSButton } from "~/features/cms/components/cmsButton/CMSButton";
import { CMSLink } from "~/features/cms/components/cmsLink/CMSLink";
import { CMSList } from "~/features/cms/components/cmsList/CMSList";
import { CMSStandaloneImage } from "~/features/cms/components/cmsMedia/CMSStandaloneMedia";
import { CMSTeasers } from "~/features/cms/components/cmsTeaser/CMSTeasers";
import CMSTeaser from "~/features/cms/components/cmsTeaser/CMSTeaser";
import { CMSBanner } from "~/features/cms/components/cmsBanner/CMSBanner";
import { CMSRotatingBanners } from "~/features/cms/components/cmsBanner/CMSRotatingBanners";
import { CMSFilterableTileContainer } from "~/features/cms/components/cmsFilterableTile/CMSFilterableTileContainer";
import { CMSLoginCall } from "~/features/cms/components/cmsLogin/CMSLoginCall";
import { CMSQuote } from "~/features/cms/components/cmsQuote/CMSQuote";
import { CMSColumnContainer } from "~/features/cms/components/cmsColumn/CMSColumnContainer";
import { CMSColumn } from "~/features/cms/components/cmsColumn/CMSColumn";
import { CMSAccordionContainer } from "~/features/cms/components/cmsAccordionContainer/CMSAccordionContainer";
import { CMSAccordion } from "~/features/cms/components/cmsAccordion/CMSAccordion";
import { CMSPageNavigation } from "~/features/cms/components/cmsPageNavigation/CMSPageNavigation";
import { CMSNewsletterConsent } from "~/features/cms/components/cmsNewsletter/CMSNewsletterConsent";
import { CMSPhoto } from "~/features/cms/components/cmsImage/CMSImage";
import { CMSPhotoGallery } from "~/features/cms/components/cmsImage/CMSPhotoGallery";
import { CMSColumnLinks } from "~/features/cms/components/cmsColumn/CMSColumnLinks";
import { EwellixPageNavigationComponent } from "~/features/cms/components/cmsPageNavigation/EwellixPageNavigationComponent";
import { CMSTable } from "~/features/cms/components/cmsTable/CMSTable";
import { SchaefflerForm } from "~/features/cms/schaefflerForm/SchaefflerForm";
import { SchaefflerFormHiddenField } from "~/features/cms/schaefflerForm/SchaefflerFormHiddenField";
import { SchaefflerFormSelectField } from "~/features/cms/schaefflerForm/SchaefflerFormSelectField";
import { SchaefflerFormTextField } from "~/features/cms/schaefflerForm/SchaefflerFormTextField";
import { SchaefflerFormCheckboxField } from "~/features/cms/schaefflerForm/SchaefflerFormCheckboxField";
import { CMSCardComponent } from "~/features/cms/components/cmsCard/CMSCardComponent";
import { CMSHomepagePersonalization } from "~/features/cms/homepagePersonalization/CMSHomepagePersonalization";
import { CMSChipLink } from "~/features/cms/components/cmsChip/CMSChipLink";
import { CMSChipList } from "~/features/cms/components/cmsChip/CMSChipList";
import { CMSParagraph } from "~/features/cms/components/cmsParagraph/CMSParagraph";
import CMSKameleoonSlot from "~/features/cms/components/cmsKameleoon/CMSKameleoonSlot";
import { CMSIframe } from "./components/cmsIframe/CMSIframe";
import { CMSSimpleResponsiveBanner } from "~/features/cms/components/cmsResponsiveBanner/CMSSimpleResponsiveBanner";
import { CMSProductCarousel } from "~/features/cms/components/cmsProductCarousel/CMSProductCarousel";
import { CMSSAPRecommendation } from "~/features/cms/components/cmsSapRecommendation/CMSSAPRecommendation";
import { CMSImageHeader } from "~/features/cms/components/cmsImageHeader/CMSImageHeader";
import { CMSSimpleBanner } from "~/features/cms/components/cmsSimpleBanner/CMSSimpleBanner";
import { CMSRotatingImages } from "~/features/cms/components/cmsRotatingImages/CMSRotatingImages";
import { CMSImageTile } from "~/features/cms/components/cmsImageTile/CMSImageTile";
import { CMSImageTileContent } from "~/features/cms/components/cmsImageTileContent/CMSImageTileContent";
import { CMSForkPageNavigation } from "~/features/cms/components/cmsForkPageNavigation/CMSForkPageNavigation";
import { CMSCadFileSearch } from "~/features/cms/components/cmsCadFileSearch/CMSCadFileSearch";
import { CMSFccRecommendation } from "~/features/cms/components/cmsFccRecommendation/CMSFccRecommendation";
import { CMSHeaterSelectionAssistant } from "~/features/cms/components/cmsHeaterSelectionAssistant/CMSHeaterSelectionAssistant";

const COMPONENT_MAP: {
  [K in CMSComponentProps["type"]]: ComponentType<Extract<CMSComponentProps, { type: K }>>;
} = {
  [ComponentTypes.CMSSection]: CMSSection,
  [ComponentTypes.CMSStandaloneMedia]: CMSStandaloneImage,
  [ComponentTypes.CMSList]: CMSList,
  [ComponentTypes.CMSButton]: CMSButton,
  [ComponentTypes.CMSLink]: CMSLink,
  [ComponentTypes.CMSTeaserContainer]: CMSTeasers,
  [ComponentTypes.CMSTeaser]: CMSTeaser as ComponentType<CMSTeaserStandardProps>,
  [ComponentTypes.CMSPublicationTeaser]: CMSTeaser as ComponentType<CMSTeaserPublicationProps>,
  [ComponentTypes.CMSBanner]: CMSBanner,
  [ComponentTypes.CMSRotatingBanners]: CMSRotatingBanners,
  [ComponentTypes.CMSFilterableTileContainer]: CMSFilterableTileContainer,
  [ComponentTypes.CMSParagraph]: CMSParagraph,
  [ComponentTypes.CMSLoginCall]: CMSLoginCall,
  [ComponentTypes.CMSQuote]: CMSQuote,
  [ComponentTypes.CMSColumnsContainer]: CMSColumnContainer,
  [ComponentTypes.CMSColumn]: CMSColumn,
  [ComponentTypes.CMSAccordionContainer]: CMSAccordionContainer,
  [ComponentTypes.CMSAccordion]: CMSAccordion,
  [ComponentTypes.CMSTable]: CMSTable,
  [ComponentTypes.CMSPageNavigation]: CMSPageNavigation,
  [ComponentTypes.CMSNewsLetterConsent]: CMSNewsletterConsent,
  [ComponentTypes.CMSPhotoGallery]: CMSPhotoGallery,
  [ComponentTypes.CMSPhoto]: CMSPhoto,
  [ComponentTypes.CMSColumnLinks]: CMSColumnLinks,
  [ComponentTypes.EwellixPageNavigation]: EwellixPageNavigationComponent,
  [ComponentTypes.Form]: SchaefflerForm,
  [ComponentTypes.FormHiddenField]: SchaefflerFormHiddenField,
  [ComponentTypes.FormFieldSelect]: SchaefflerFormSelectField,
  [ComponentTypes.FormFieldText]: SchaefflerFormTextField,
  [ComponentTypes.FormFieldCheckbox]: SchaefflerFormCheckboxField,
  [ComponentTypes.CMSCardComponent]: CMSCardComponent,
  [ComponentTypes.CMSHomepagePersonalization]: CMSHomepagePersonalization,
  [ComponentTypes.CMSChipLink]: CMSChipLink,
  [ComponentTypes.CMSChipList]: CMSChipList,
  [ComponentTypes.CMSKameleoonSlot]: CMSKameleoonSlot,
  [ComponentTypes.CMSIframe]: CMSIframe,
  [ComponentTypes.CMSSimpleResponsiveBanner]: CMSSimpleResponsiveBanner,
  [ComponentTypes.CMSProductCarousel]: CMSProductCarousel,
  [ComponentTypes.CMSSAPRecommendation]: CMSSAPRecommendation,
  [ComponentTypes.CMSImageHeader]: CMSImageHeader,
  [ComponentTypes.CMSSimpleBanner]: CMSSimpleBanner,
  [ComponentTypes.CMSRotatingImages]: CMSRotatingImages,
  [ComponentTypes.CMSImageTile]: CMSImageTile,
  [ComponentTypes.CMSImageTileContent]: CMSImageTileContent,
  [ComponentTypes.CMSForkPageNavigation]: CMSForkPageNavigation,
  [ComponentTypes.CMSCadFileSearch]: CMSCadFileSearch,
  [ComponentTypes.CMSFccRecommendation]: CMSFccRecommendation,
  [ComponentTypes.CMSHeaterSelectionAssistant]: CMSHeaterSelectionAssistant,
} as const;

export const canRenderCMSComponent = (type: string): boolean =>
  Boolean(COMPONENT_MAP[type as CMSComponentProps["type"]]);

const renderCMSComponentInternal: CMSComponentRenderer = (props, className, key) => {
  const Component = COMPONENT_MAP[props.type];

  if (!Component) {
    console.error(`Could not find a component for type ${props.type}`);
    return null;
  }

  // @ts-ignore
  return <Component key={key} className={className} {...props} />;
};

initializeCMSComponentRenderer(renderCMSComponentInternal);

export const renderCMSComponent: CMSComponentRenderer = (props, className, key) => {
  const component = renderCMSComponentInternal(props, className, key);
  if (!component) {
    return null;
  }
  return <CMSComponentRendererProvider renderer={renderCMSComponentInternal}>{component}</CMSComponentRendererProvider>;
};
